<template>
  <modal
    :name="modalName"
    :width="'98%'"
    :height="'90%'"
    :max-width="1920"
    :scrollable="true"
    :adaptive="true"
    :click-to-close="false"
    :reset="true"
    class="communication-review-modal modal-not-overflow"
    @opened="focusHeaderClose"
    @before-close="onBeforeClose"
  >
    <div
      class="communication-review"
      role="dialog"
      aria-modal="true"
      aria-labelledby="communication-review-title"
    >
      <AppModalHeader
        ref="modalHeader"
        design="new"
        :eyebrow="flowTitle"
        :title="reviewTitle"
        title-id="communication-review-title"
        @close="close"
      >
        <template v-if="detail" #eyebrow>
          <span class="communication-review__eyebrow-content">
            <span>{{ flowTitle }}</span>
            <AppStatusBadge
              design="new"
              compact
              :tone="matchTone(detail.match_type.code)"
              :label="friendlyMatchName"
            />
          </span>
        </template>
      </AppModalHeader>

      <div v-if="detailLoading" class="communication-review__state">
        <app-spinner design="new" />
      </div>
      <div v-else-if="detailError" class="communication-review__state communication-review__state--error">
        <strong>{{ detailError }}</strong>
        <app-button design="new" theme="dark" @click.native="loadDetail">Retry</app-button>
      </div>

      <template v-else-if="detail">
        <div class="communication-review__body">
          <div class="communication-review__top-grid">
            <AppCard design="new" padding="none" class="communication-review__block communication-review__summary-card">
              <header class="communication-review__summary-card-title">
                <h3>Conversation</h3>
                <AppStatusBadge
                  design="new"
                  compact
                  :tone="ticketStatusTone(detail.ticket.status.code)"
                  :label="detail.ticket.status.name"
                />
              </header>
              <dl class="communication-review__summary-list">
                <div>
                  <dt>Ticket</dt>
                  <dd class="communication-review__ticket-value">
                    <span>#{{ detail.ticket.external_id }}</span>
                    <a
                      v-if="trengoTicketUrl"
                      v-tooltip="{ content: 'Open in Trengo', placement: 'top', offset: 8, classes: 'tooltip--new' }"
                      :href="trengoTicketUrl"
                      class="communication-review__external-link"
                      aria-label="Open in Trengo"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <svg viewBox="0 0 16 16" aria-hidden="true">
                        <path d="M9 2h5v5M14 2 7.5 8.5M12.5 9.5v3a1.5 1.5 0 0 1-1.5 1.5H3.5A1.5 1.5 0 0 1 2 12.5V5a1.5 1.5 0 0 1 1.5-1.5h3" />
                      </svg>
                    </a>
                  </dd>
                </div>
                <div>
                  <dt>Assigned to</dt>
                  <dd>{{ managerName(ticketAssignee) }}</dd>
                </div>
                <div>
                  <dt>Channel</dt>
                  <dd class="communication-review__channel-value">
                    <span class="communication-review__block-icon communication-review__block-icon--wa">
                      <img src="@/assets/img/wa-round-icon.svg" alt="" aria-hidden="true">
                    </span>
                    <span>{{ channelName }}</span>
                  </dd>
                </div>
              </dl>
            </AppCard>

            <AppCard design="new" padding="none" class="communication-review__block communication-review__summary-card">
              <header class="communication-review__summary-card-title">
                <h3>Activity</h3>
              </header>
              <dl class="communication-review__summary-list">
                <div>
                  <dt>Started</dt>
                  <dd>{{ compactDate(detail.ticket.inserted_at) }}</dd>
                </div>
                <div>
                  <dt>Last activity</dt>
                  <dd>{{ compactDate(detail.ticket.last_activity_at) }}</dd>
                </div>
                <div>
                  <dt>Messages</dt>
                  <dd>{{ detail.ticket.message_count }}</dd>
                </div>
              </dl>
            </AppCard>

            <AppCard design="new" padding="none" class="communication-review__block communication-review__summary-card">
              <header class="communication-review__summary-card-title">
                <h3>Customer info</h3>
                <app-button
                  design="new"
                  theme="dark"
                  class="communication-review__contact-refresh"
                  size="small"
                  :disabled="contactRefreshLoading || isLinked"
                  @click.native="refreshContact"
                >
                  {{ contactRefreshLoading ? 'Refreshing…' : 'Refresh from Trengo' }}
                </app-button>
              </header>
              <dl class="communication-review__summary-list communication-review__summary-list--customer">
                <div>
                  <dt>Customer</dt>
                  <dd><span>{{ detail.contact && detail.contact.name || 'Unknown customer' }}</span></dd>
                </div>
                <div>
                  <dt>Email</dt>
                  <dd>
                    <span>{{ detail.contact && detail.contact.email || 'Not provided' }}</span>
                    <app-button
                      v-if="detail.contact && detail.contact.email"
                      design="new"
                      theme="dark"
                      size="small"
                      class="communication-review__copy-action"
                      aria-label="Copy email"
                      v-tooltip="{ content: 'Copy email', placement: 'top', offset: 8, classes: 'tooltip--new' }"
                      @click.native="copy(detail.contact.email)"
                    >
                      <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
                        <rect x="6" y="6" width="9" height="9" rx="1.5" stroke="currentColor" stroke-width="1.5" />
                        <path d="M4 12H3a1.5 1.5 0 0 1-1.5-1.5V3A1.5 1.5 0 0 1 3 1.5h7.5A1.5 1.5 0 0 1 12 3v1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                      </svg>
                    </app-button>
                  </dd>
                </div>
                <div>
                  <dt>Phone</dt>
                  <dd>
                    <span>{{ detail.contact && detail.contact.phone || 'Not provided' }}</span>
                    <app-button
                      v-if="detail.contact && detail.contact.phone"
                      design="new"
                      theme="dark"
                      size="small"
                      class="communication-review__copy-action"
                      aria-label="Copy phone"
                      v-tooltip="{ content: 'Copy phone', placement: 'top', offset: 8, classes: 'tooltip--new' }"
                      @click.native="copy(detail.contact.phone)"
                    >
                      <svg viewBox="0 0 18 18" fill="none" aria-hidden="true">
                        <rect x="6" y="6" width="9" height="9" rx="1.5" stroke="currentColor" stroke-width="1.5" />
                        <path d="M4 12H3a1.5 1.5 0 0 1-1.5-1.5V3A1.5 1.5 0 0 1 3 1.5h7.5A1.5 1.5 0 0 1 12 3v1" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
                      </svg>
                    </app-button>
                  </dd>
                </div>
              </dl>
            </AppCard>
          </div>

          <div class="communication-review__workspace">
            <AppCard design="new" padding="none" class="communication-review__block communication-review__timeline">
              <header class="communication-review__workspace-title">
                <div>
                  <small>Timeline</small>
                  <h3>Conversation</h3>
                </div>
                <span>{{ messages.length }} loaded</span>
              </header>
              <app-button
                v-if="messagesNextCursor"
                design="new"
                theme="dark"
                class="communication-review__older"
                size="small"
                :disabled="messagesLoading"
                @click.native="loadOlderMessages"
              >
                {{ messagesLoading ? 'Loading…' : 'Show older messages' }}
              </app-button>
              <p v-if="messagesError" class="communication-review__inline-error">{{ messagesError }}</p>
              <div class="communication-review__messages">
                <div v-if="timelineDateLabel" class="communication-review__timeline-date">
                  <span>{{ timelineDateLabel }}</span>
                </div>
                <article
                  v-for="message in messages"
                  :key="message.id"
                  class="communication-review__message"
                  :class="messageClass(message)"
                >
                  <div class="communication-review__message-bubble">
                    <span v-if="messageSender(message)" class="communication-review__sender">{{ messageSender(message) }}</span>
                    <p>{{ message.body || message.body_text || 'No text content' }}</p>
                  </div>
                  <time
                    v-tooltip="{ content: exactDate(message.inserted_at), placement: 'top', offset: 8, classes: 'tooltip--new' }"
                  >{{ relativeDate(message.inserted_at) }}</time>
                </article>
                <div v-if="!messages.length" class="communication-review__empty">
                  No imported messages yet.
                </div>
              </div>
            </AppCard>

            <AppCard design="new" padding="none" class="communication-review__block communication-review__decision">
              <header class="communication-review__workspace-title communication-review__decision-title">
                <div>
                  <small>Decision</small>
                  <h3>{{ decisionTitle }}</h3>
                  <p v-if="decisionExplanation">{{ decisionExplanation }}</p>
                </div>
                <div>
                  <span v-if="candidateCount" class="communication-review__candidate-count">
                    {{ candidateCount }} {{ candidateCount === 1 ? 'candidate' : 'candidates' }}
                  </span>
                  <app-button
                    v-if="!isIgnored && !searchMode && candidates.length"
                    design="new"
                    theme="dark"
                    size="small"
                    @click.native="enableSearch"
                  >
                    Search leads
                  </app-button>
                </div>
              </header>

              <div v-if="isIgnored" class="communication-review__ignored-details">
                <div><span>Reason</span><strong>{{ ignoreReason }}</strong></div>
                <div v-if="ignoreNote"><span>Note</span><strong>{{ ignoreNote }}</strong></div>
                <div><span>Ignored by</span><strong>{{ ignoredBy }}</strong></div>
                <div><span>Ignored at</span><strong>{{ compactDate(ignoredAt) }}</strong></div>
              </div>

              <div v-if="isLinked" class="communication-review__lead-list">
                <LeadCard
                  v-if="linkedLead"
                  :lead="linkedLead"
                  :flow-version="flowVersion"
                  :selected="false"
                  :relative-date="relativeDate"
                />
              </div>
              <template v-else-if="!isIgnored && (searchMode || !candidates.length)">
                <app-input
                  v-model="leadSearch"
                  design="new"
                  type="search"
                  placeholder="Search by lead ID, name, email or phone"
                  @input.native="scheduleLeadSearch"
                />
                <p v-if="leadSearchLoading" class="communication-review__loading">Searching leads…</p>
                <p v-if="leadSearchError" class="communication-review__inline-error">{{ leadSearchError }}</p>
                <div class="communication-review__lead-list">
                  <LeadCard
                    v-for="lead in leadOptions"
                    :key="lead.id"
                    :lead="lead"
                    :flow-version="flowVersion"
                    :selected="selectedLeadId === lead.id"
                    :relative-date="relativeDate"
                    @select="selectLead"
                  />
                </div>
                <app-button
                  v-if="leadOptionsCursor"
                  design="new"
                  theme="dark"
                  class="communication-review__more"
                  size="small"
                  :disabled="leadSearchLoading"
                  @click.native="loadLeadOptions(true)"
                >
                  Show more
                </app-button>
              </template>
              <div v-else-if="!isIgnored" class="communication-review__lead-list">
                <LeadCard
                  v-for="lead in candidates"
                  :key="lead.id"
                  :lead="lead"
                  :flow-version="flowVersion"
                  :selected="selectedLeadId === lead.id"
                  :relative-date="relativeDate"
                  @select="selectLead"
                />
              </div>
            </AppCard>
          </div>
        </div>

        <footer class="communication-review__actions">
          <template v-if="isLinked">
            <router-link
              v-if="linkedLead"
              :to="{ name: 'leads-detail-lead', params: { id: linkedLead.id } }"
              class="communication-review__linked-lead-link"
              target="_blank"
            >
              <app-button design="new" theme="dark">Open linked lead</app-button>
            </router-link>
          </template>
          <template v-else-if="isReopenable">
            <app-button
              design="new"
              :disabled="actionLoading"
              @click.native="reopen"
            >
              {{ actionLoading ? 'Saving…' : 'Reopen matching' }}
            </app-button>
          </template>
          <template v-else>
            <app-button
              design="new"
              theme="error"
              :disabled="actionLoading"
              @click.native="openIgnoreDialog"
            >
              Ignore ticket
            </app-button>
            <app-button
              design="new"
              theme="success"
              :disabled="actionLoading"
              @click.native="openCreateLeadDialog"
            >
              {{ actionLoading ? 'Saving…' : (createsSharedLead ? 'Create lead in shared pool' : 'Create V2 lead') }}
            </app-button>
            <app-button
              design="new"
              :disabled="!selectedLeadId || actionLoading"
              @click.native="link"
            >
              {{ actionLoading ? 'Saving…' : 'Link selected lead' }}
            </app-button>
          </template>
        </footer>
      </template>

      <div v-if="createLeadDialogOpen" class="communication-review__dialog-backdrop" @click.self="closeCreateLeadDialog">
        <form
          class="communication-review__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="communication-create-lead-title"
          @keydown.esc.stop.prevent="closeCreateLeadDialog"
          @submit.prevent="createV2Lead"
        >
          <AppModalHeader
            ref="createLeadModalHeader"
            design="new"
            :title="createsSharedLead ? 'Create lead in shared pool' : 'Create V2 lead'"
            title-id="communication-create-lead-title"
            @close="closeCreateLeadDialog"
          />
          <div class="communication-review__dialog-body">
            <p>Select the activities and language for this lead.</p>
            <p v-if="createLeadError" role="alert" class="communication-review__inline-error">{{ createLeadError }}</p>
            <app-form-group
              design="new"
              label="Activity"
              required
              class="communication-review__dialog-field form-group--no-indent"
            >
              <fieldset class="communication-review__activities" :disabled="actionLoading" aria-label="Activity">
                <app-checkbox
                  v-for="activity in activityOptions"
                  :key="activity.code"
                  v-model="createLeadForm[activity.code]"
                  design="new"
                  :label="activity.name"
                />
              </fieldset>
            </app-form-group>
            <app-form-group
              design="new"
              label="Language"
              required
              class="communication-review__dialog-field form-group--no-indent"
            >
              <app-select
                v-model="createLeadForm.language"
                design="new"
                aria-label="Lead language"
                :options="languageOptions"
                :reduce="language => language.code"
                :disabled="actionLoading || languagesLoading"
                :clearable="false"
                :loading="languagesLoading"
                placeholder="Select language"
              />
              <template v-if="languagesError" #error>
                {{ languagesError }}
                <app-button design="new" theme="dark" size="small" @click.native="loadLanguages">Retry</app-button>
              </template>
            </app-form-group>
          </div>
          <div class="communication-review__dialog-actions">
            <app-button design="new" theme="dark" :disabled="actionLoading" @click.native="closeCreateLeadDialog">Cancel</app-button>
            <app-button design="new" type="submit" :disabled="!canCreateLead">
              {{ actionLoading ? 'Saving…' : 'Save' }}
            </app-button>
          </div>
        </form>
      </div>

      <div v-if="ignoreDialogOpen" class="communication-review__dialog-backdrop" @click.self="closeIgnoreDialog">
        <form
          class="communication-review__dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="communication-ignore-title"
          @keydown.esc.stop.prevent="closeIgnoreDialog"
          @submit.prevent="ignore"
        >
          <AppModalHeader
            ref="ignoreModalHeader"
            design="new"
            title="Ignore ticket"
            title-id="communication-ignore-title"
            @close="closeIgnoreDialog"
          />
          <div class="communication-review__dialog-body">
            <p>This conversation will move to the Ignored queue.</p>
            <app-form-group
              design="new"
              label="Reason"
              class="communication-review__dialog-field form-group--no-indent"
            >
              <app-select
                v-model="ignoreForm.reason"
                design="new"
                :options="ignoreReasons"
                :reduce="reason => reason.code"
                :clearable="false"
                :searchable="false"
              />
            </app-form-group>
            <app-form-group
              design="new"
              label="Note"
              class="communication-review__dialog-field form-group--no-indent"
            >
              <app-textarea
                v-model="ignoreForm.note"
                design="new"
                rows="4"
                placeholder="Add context for other reviewers"
              />
              <template v-if="ignoreForm.reason === 'other' && !ignoreForm.note.trim()" #error>
                A note is required for Other.
              </template>
            </app-form-group>
          </div>
          <div class="communication-review__dialog-actions">
            <app-button design="new" theme="dark" @click.native="closeIgnoreDialog">Cancel</app-button>
            <app-button
              design="new"
              type="submit"
              theme="error"
              :disabled="actionLoading || (ignoreForm.reason === 'other' && !ignoreForm.note.trim())"
            >
              Ignore
            </app-button>
          </div>
        </form>
      </div>
    </div>
  </modal>
</template>

<script>
import { format, formatDistanceToNow } from 'date-fns'
import {
  getCommunicationReconciliationDetail,
  getCommunicationReconciliationLeadOptions,
  getCommunicationReconciliationMessages,
  getLanguages,
  postCommunicationReconciliationCreateV2Lead,
  postCommunicationReconciliationIgnore,
  postCommunicationReconciliationLink,
  postCommunicationReconciliationRefreshContact,
  postCommunicationReconciliationReopen
} from '@/http'
import LeadCard from './LeadCard.vue'

const SHARED_LEAD_CHANNEL_EXTERNAL_ID = '1119384'

export default {
  name: 'CommunicationReconciliationReviewModal',
  components: { LeadCard },
  props: {
    ticketId: { type: Number, required: true },
    flowVersion: { type: String, required: true }
  },
  data() {
    return {
      modalName: 'communication-reconciliation-review',
      detail: null,
      detailLoading: false,
      detailError: '',
      messages: [],
      messagesNextCursor: null,
      messagesLoading: false,
      messagesError: '',
      selectedLeadId: null,
      searchMode: false,
      leadSearch: '',
      leadOptions: [],
      leadOptionsCursor: null,
      leadSearchLoading: false,
      leadSearchError: '',
      actionLoading: false,
      contactRefreshLoading: false,
      createLeadDialogOpen: false,
      createLeadError: '',
      createLeadForm: { climbing: false, safari: false, day_trip: false, island: false, language: null },
      activityOptions: [
        { code: 'safari', name: 'Safari' },
        { code: 'climbing', name: 'Climbing' },
        { code: 'day_trip', name: 'Day Trip' },
        { code: 'island', name: 'Vacation Island' }
      ],
      languageOptions: [],
      languagesLoading: false,
      languagesError: '',
      ignoreDialogOpen: false,
      ignoreReasons: [
        { code: 'spam', name: 'Spam' },
        { code: 'not_customer', name: 'Not a customer' },
        { code: 'irrelevant', name: 'Irrelevant conversation' },
        { code: 'group_request', name: 'Part of a group request' },
        { code: 'other', name: 'Other' }
      ],
      ignoreForm: { reason: 'spam', note: '' },
      leadSearchTimer: null,
      detailRequestId: 0,
      messagesRequestId: 0,
      leadSearchRequestId: 0
    }
  },
  computed: {
    canCreateLead() {
      return Boolean(
        this.detail && !this.isLinked && !this.actionLoading && !this.languagesLoading && !this.languagesError
        && this.languageOptions.some(language => language.code === this.createLeadForm.language)
        && this.activityOptions.some(activity => this.createLeadForm[activity.code])
      )
    },
    createsSharedLead() {
      const ticket = this.detail && this.detail.ticket
      return Boolean(
        ticket &&
        ticket.status.code === 'OPEN' &&
        !ticket.assignee &&
        ticket.channel &&
        ticket.channel.external_id === SHARED_LEAD_CHANNEL_EXTERNAL_ID
      )
    },
    trengoTicketUrl() {
      const externalId = this.detail && this.detail.ticket && this.detail.ticket.external_id
      if (externalId === null || externalId === undefined || String(externalId).trim() === '') return ''
      return `https://app.trengo.com/tickets/${ encodeURIComponent(String(externalId).trim()) }`
    },
    flowTitle() {
      return this.flowVersion === 'legacy' ? 'Trengo · Matching Legacy' : 'Trengo · Matching'
    },
    reviewTitle() {
      if (!this.detail) return 'Conversation review'
      return this.detail.ticket.subject || `Ticket #${ this.detail.ticket.external_id }`
    },
    channelName() {
      const channel = this.detail.ticket.channel
      if (!channel) return 'Unknown channel'
      if (channel.display_name || channel.title) {
        return channel.display_name || channel.title
      }
      const name = channel.name || channel.channel_type || channel.type || channel.code
      if (!name) return 'Unknown channel'
      const externalId = channel.external_id
      return externalId ? `${ name } · #${ externalId }` : name
    },
    candidateCount() {
      return this.detail ? this.detail.candidate_count : 0
    },
    candidates() {
      return this.detail && this.detail.candidates ? this.detail.candidates : []
    },
    friendlyMatchName() {
      if (!this.detail) return ''
      if (this.detail.match_type.code === 'ambiguous') return 'Multiple possible leads'
      return this.detail.match_type.name
    },
    decisionTitle() {
      if (!this.detail) return ''
      const code = this.detail.match_type.code
      if (code === 'linked') return 'Linked lead'
      if (code === 'suggested') return 'Suggested lead'
      if (code === 'ambiguous') return 'Choose between possible leads'
      if (code === 'tour_operator') return 'Review Tour Operator leads'
      if (code === 'ignored') return 'Matching ignored'
      return 'Find the right lead'
    },
    decisionExplanation() {
      if (!this.detail) return ''
      const signals = this.detail.match_signals || {}
      const code = this.detail.match_type.code
      const blockingReason = signals.blocking_reason
      const identities = []
      if (signals.email_match) identities.push('email')
      if (signals.phone_match) identities.push('phone')
      if (code === 'ambiguous') {
        if (blockingReason === 'completed_with_other_status') {
          return 'A Completed lead and another active lead share these contact details. Select the correct request manually.'
        }
        const identityText = identities.length ? ` by ${ identities.join(' and ') }` : ''
        return `We found ${ this.candidateCount } leads with matching details${ identityText }. Compare their status and stage before linking.`
      }
      if (code === 'tour_operator') {
        return 'At least one matching lead belongs to a Tour Operator. Automatic linking is disabled; select the correct lead manually.'
      }
      if (code === 'suggested') {
        return identities.length ? `Recommended from the strongest ${ identities.join(' and ') } match and lead priority.` : 'Recommended by the current matching policy.'
      }
      if (code === 'ignored') return 'Reopen matching to review candidates again.'
      return ''
    },
    isIgnored() {
      if (!this.detail) return false
      const issueState = this.detail.issue && this.detail.issue.state
      return Boolean(
        (issueState && (issueState.code || issueState) === 'ignored')
        || this.detail.match_type.code === 'ignored'
      )
    },
    isLinked() {
      return Boolean(this.detail && this.detail.linked_lead)
    },
    linkedLead() {
      return this.detail ? this.detail.linked_lead : null
    },
    isReopenable() {
      return Boolean(this.isIgnored || (this.detail && this.detail.match_type.code === 'rejected'))
    },
    ticketAssignee() {
      if (!this.detail) return null
      return this.detail.ticket.assignee || null
    },
    timelineDateLabel() {
      if (!this.messages.length || !this.messages[0].inserted_at) return ''
      const messageDate = new Date(this.messages[0].inserted_at)
      const isToday = format(messageDate, 'yyyy-MM-dd') === format(new Date(), 'yyyy-MM-dd')
      return `${ isToday ? 'Today · ' : '' }${ format(messageDate, 'd MMM yyyy') }`
    },
    issue() {
      return this.detail && this.detail.issue ? this.detail.issue : {}
    },
    ignoreReason() {
      const reason = this.issue.ignore_reason || this.issue.reason
      const code = reason && (reason.code || reason)
      const labels = {
        spam: 'Spam',
        not_customer: 'Not a customer',
        irrelevant: 'Irrelevant',
        group_request: 'Part of a group request',
        other: 'Other'
      }
      return (reason && (reason.name || reason.label)) || labels[code] || code || 'Not specified'
    },
    ignoreNote() {
      return this.issue.ignore_note || this.issue.note || ''
    },
    ignoredBy() {
      return this.managerName(this.issue.ignored_by || this.issue.updated_by)
    },
    ignoredAt() {
      return this.issue.ignored_at || this.issue.updated_at || null
    }
  },
  mounted() {
    this.$modal.show(this.modalName)
    this.loadDetail()
  },
  beforeDestroy() {
    window.clearTimeout(this.leadSearchTimer)
    this.invalidateRequests()
  },
  methods: {
    focusHeaderClose() {
      this.$nextTick(() => {
        if (this.$refs.modalHeader) this.$refs.modalHeader.focusClose()
      })
    },
    close() {
      if (this.createLeadDialogOpen && this.actionLoading) return
      if (!this._isDestroyed) this.$emit('close')
    },
    onBeforeClose(event) {
      event.cancel()
      this.close()
    },
    invalidateRequests() {
      this.detailRequestId += 1
      this.messagesRequestId += 1
      this.leadSearchRequestId += 1
    },
    async loadDetail() {
      if (!this.ticketId) return
      const ticketId = this.ticketId
      const requestId = ++this.detailRequestId
      this.detailLoading = true
      this.detailError = ''
      try {
        const { data } = await getCommunicationReconciliationDetail(this.flowVersion, ticketId)
        if (requestId !== this.detailRequestId || ticketId !== this.ticketId) return
        this.applyDetail(data)
      } catch (error) {
        if (requestId === this.detailRequestId) this.detailError = this.errorMessage(error, 'Unable to load conversation details')
      } finally {
        if (requestId === this.detailRequestId) this.detailLoading = false
      }
    },
    async loadOlderMessages() {
      if (!this.ticketId || !this.messagesNextCursor) return
      const ticketId = this.ticketId
      const requestId = ++this.messagesRequestId
      this.messagesLoading = true
      this.messagesError = ''
      try {
        const { data } = await getCommunicationReconciliationMessages(this.flowVersion, ticketId, { cursor: this.messagesNextCursor })
        if (requestId !== this.messagesRequestId || ticketId !== this.ticketId) return
        const merged = data.results.slice().reverse().concat(this.messages)
        const seen = new Set()
        this.messages = merged.filter(message => {
          if (seen.has(message.id)) return false
          seen.add(message.id)
          return true
        })
        this.messagesNextCursor = data.next_cursor
      } catch (error) {
        if (requestId === this.messagesRequestId) this.messagesError = this.errorMessage(error, 'Unable to load older messages')
      } finally {
        if (requestId === this.messagesRequestId) this.messagesLoading = false
      }
    },
    enableSearch() {
      this.searchMode = true
      this.leadSearch = ''
      this.leadOptions = []
      this.leadOptionsCursor = null
      this.loadLeadOptions(false)
    },
    scheduleLeadSearch() {
      window.clearTimeout(this.leadSearchTimer)
      this.leadSearchRequestId += 1
      this.leadSearchLoading = false
      this.leadOptions = []
      this.leadOptionsCursor = null
      this.leadSearchTimer = window.setTimeout(() => this.loadLeadOptions(false), 350)
    },
    async loadLeadOptions(append) {
      if (!this.ticketId || this.leadSearchLoading) return
      const ticketId = this.ticketId
      const requestId = ++this.leadSearchRequestId
      this.leadSearchLoading = true
      this.leadSearchError = ''
      try {
        const params = {}
        const search = String(this.leadSearch || '').trim()
        if (search) params.search = search
        if (append) params.cursor = this.leadOptionsCursor
        const { data } = await getCommunicationReconciliationLeadOptions(this.flowVersion, ticketId, params)
        if (requestId !== this.leadSearchRequestId || ticketId !== this.ticketId) return
        this.leadOptions = append ? this.leadOptions.concat(data.results) : data.results
        this.leadOptionsCursor = data.next_cursor
      } catch (error) {
        if (requestId === this.leadSearchRequestId) this.leadSearchError = this.errorMessage(error, 'Unable to search leads')
      } finally {
        if (requestId === this.leadSearchRequestId) this.leadSearchLoading = false
      }
    },
    selectLead(lead) {
      this.selectedLeadId = lead.id
    },
    applyDetail(data) {
      this.detail = data
      this.selectedLeadId = null
      this.leadOptions = []
      this.leadOptionsCursor = null
      this.leadSearchError = ''
      this.messages = data.recent_messages || []
      this.messagesNextCursor = data.messages_next_cursor
      this.searchMode = ['no_match', 'rejected', 'unprocessed'].includes(data.match_type.code)
      if (data.match_type.code === 'suggested' && data.suggested_lead) this.selectedLeadId = data.suggested_lead.id
      if (this.searchMode) this.loadLeadOptions(false)
    },
    async refreshContact() {
      if (!this.detail || this.contactRefreshLoading || this.isLinked) return
      const ticketId = this.detail.ticket_id
      this.contactRefreshLoading = true
      try {
        const { data } = await postCommunicationReconciliationRefreshContact(
          this.flowVersion,
          ticketId
        )
        if (this._isDestroyed || !this.detail || this.detail.ticket_id !== ticketId) return
        this.applyDetail(data.detail)
        const titles = {
          updated: 'Customer refreshed and matching recalculated',
          unchanged: 'Customer is already up to date',
          not_found: 'Contact was not found in Trengo'
        }
        this.$notify({
          type: data.result === 'not_found' ? 'info' : 'success',
          title: titles[data.result] || 'Customer refreshed'
        })
        this.$emit('stale')
      } catch (error) {
        this.handleActionError(error, 'Unable to refresh customer from Trengo')
      } finally {
        this.contactRefreshLoading = false
      }
    },
    openCreateLeadDialog() {
      if (!this.detail || this.actionLoading || this.isLinked) return
      this.createLeadForm = { climbing: false, safari: false, day_trip: false, island: false, language: null }
      this.createLeadError = ''
      this.createLeadDialogOpen = true
      this.loadLanguages()
      this.$nextTick(() => {
        if (this.$refs.createLeadModalHeader) this.$refs.createLeadModalHeader.focusClose()
      })
    },
    closeCreateLeadDialog() {
      if (this.actionLoading) return
      this.createLeadDialogOpen = false
      this.createLeadError = ''
      this.focusHeaderClose()
    },
    async loadLanguages() {
      if (this.languagesLoading) return
      this.languagesLoading = true
      this.languagesError = ''
      try {
        const { data } = await getLanguages()
        if (!this._isDestroyed) this.languageOptions = data.filter(language => language.active !== false)
      } catch (error) {
        if (!this._isDestroyed) this.languagesError = this.errorMessage(error, 'Unable to load languages')
      } finally {
        this.languagesLoading = false
      }
    },
    async createV2Lead() {
      if (!this.createLeadDialogOpen || !this.canCreateLead) return
      this.createLeadError = ''
      this.actionLoading = true
      try {
        const { data } = await postCommunicationReconciliationCreateV2Lead(
          this.flowVersion,
          this.detail.ticket_id,
          { ...this.createLeadForm, issue_id: this.detail.issue_id }
        )
        if (this._isDestroyed) return
        this.$notify({
          type: 'success',
          title: `Lead #${ data.lead.id } created${ this.createsSharedLead ? ' in shared pool' : '' } and linked`
        })
        this.createLeadDialogOpen = false
        this.finish()
      } catch (error) {
        this.createLeadError = this.errorMessage(error, 'Unable to create V2 lead')
        this.handleActionError(error, 'Unable to create V2 lead')
      } finally {
        this.actionLoading = false
      }
    },
    async link() {
      if (!this.detail || !this.selectedLeadId || this.actionLoading) return
      this.actionLoading = true
      try {
        await postCommunicationReconciliationLink(this.flowVersion, this.detail.ticket_id, {
          lead_id: this.selectedLeadId,
          issue_id: this.detail.issue_id
        })
        if (this._isDestroyed) return
        this.$notify({ type: 'success', title: 'Conversation linked' })
        this.finish()
      } catch (error) {
        this.handleActionError(error, 'Unable to link conversation')
      } finally {
        this.actionLoading = false
      }
    },
    async ignore() {
      if (!this.detail || this.actionLoading) return
      this.actionLoading = true
      try {
        await postCommunicationReconciliationIgnore(this.flowVersion, this.detail.ticket_id, {
          reason: this.ignoreForm.reason,
          note: this.ignoreForm.note.trim(),
          issue_id: this.detail.issue_id
        })
        if (this._isDestroyed) return
        this.$notify({ type: 'success', title: 'Ticket ignored' })
        this.closeIgnoreDialog()
        this.finish()
      } catch (error) {
        this.handleActionError(error, 'Unable to ignore ticket')
      } finally {
        this.actionLoading = false
      }
    },
    async reopen() {
      if (!this.detail || this.actionLoading) return
      this.actionLoading = true
      try {
        await postCommunicationReconciliationReopen(this.flowVersion, this.detail.ticket_id, { issue_id: this.detail.issue_id })
        if (this._isDestroyed) return
        this.$notify({ type: 'success', title: 'Ticket reopened' })
        this.finish()
      } catch (error) {
        this.handleActionError(error, 'Unable to reopen ticket')
      } finally {
        this.actionLoading = false
      }
    },
    finish() {
      this.close()
      this.$emit('resolved')
    },
    handleActionError(error, fallback) {
      if (this._isDestroyed) return
      this.$notify({ type: 'error', title: this.errorMessage(error, fallback) })
      if (error.response && [400, 409].includes(error.response.status)) {
        this.selectedLeadId = null
        this.loadDetail()
        this.$emit('stale')
      }
    },
    closeIgnoreDialog() {
      this.ignoreDialogOpen = false
      this.ignoreForm = { reason: 'spam', note: '' }
    },
    openIgnoreDialog() {
      this.ignoreDialogOpen = true
      this.$nextTick(() => {
        if (this.$refs.ignoreModalHeader) this.$refs.ignoreModalHeader.focusClose()
      })
    },
    messageClass(message) {
      const direction = String((message.direction && (message.direction.code || message.direction)) || '').toLowerCase()
      return `communication-review__message--${ direction }`
    },
    messageSender(message) {
      const trengoSender = message.sender || message.trengo_sender
      const boardUser = message.mapped_user || message.user
      const names = [this.managerName(trengoSender), this.managerName(boardUser)]
        .filter(name => name !== 'Unassigned')
      return [...new Set(names)].join(' · ')
    },
    managerName(manager) {
      return manager && (manager.name || manager.full_name) ? manager.name || manager.full_name : 'Unassigned'
    },
    matchTone(code) {
      return {
        suggested: 'green',
        ambiguous: 'amber',
        tour_operator: 'amber',
        linked: 'blue',
        ignored: 'neutral',
        no_match: 'red',
        rejected: 'red',
        unprocessed: 'red'
      }[code] || 'neutral'
    },
    ticketStatusTone(code) {
      return {
        OPEN: 'green',
        ASSIGNED: 'blue',
        INVALID: 'red'
      }[String(code || '').toUpperCase()] || 'neutral'
    },
    compactDate(value) {
      return value ? format(new Date(value), 'dd MMM yyyy, HH:mm') : '—'
    },
    exactDate(value) {
      return this.compactDate(value)
    },
    relativeDate(value) {
      if (!value) return '—'
      return formatDistanceToNow(new Date(value), { addSuffix: true })
    },
    async copy(value) {
      try {
        await navigator.clipboard.writeText(value)
        this.$notify({ type: 'success', title: 'Copied' })
      } catch (error) {
        this.$notify({ type: 'error', title: 'Unable to copy' })
      }
    },
    errorMessage(error, fallback) {
      const data = error.response && error.response.data
      const detail = data && data.detail
      if (data && data.message) return data.message
      if (data && typeof data.details === 'string') return data.details
      if (detail && typeof detail === 'object') return detail.message || detail.detail || fallback
      if (typeof detail === 'string') return detail
      if (data && typeof data === 'object') {
        const fieldError = Object.entries(data).find(([key, value]) => {
          return !['code', 'message', 'detail'].includes(key) && value
        })
        if (fieldError) {
          const [field, value] = fieldError
          const message = Array.isArray(value) ? value.join(' ') : value
          return `${ field.replace(/_/g, ' ') }: ${ message }`
        }
      }
      return fallback
    }
  }
}
</script>

<style lang="scss" scoped>
.communication-review {
  position: relative;
  display: flex;
  height: 100%;
  max-height: 90vh;
  min-height: 0;
  overflow: hidden;
  flex-direction: column;
  background: $ui-surface-soft;
  color: $ui-text;
  font-family: $ui-font-body;

  &__eyebrow-content {
    display: flex;
    align-items: center;
    gap: $ui-space-8;
    flex-wrap: wrap;
  }

  &__state {
    display: flex;
    min-height: 320px;
    flex: 1;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    gap: $ui-space-12;
    font-size: $ui-font-size-body;

    &--error {
      color: $ui-red;
    }
  }

  &__body {
    display: flex;
    min-height: 0;
    flex: 1 1 auto;
    padding: $ui-space-20 $ui-space-24;
    overflow: hidden;
    flex-direction: column;
  }

  &__top-grid {
    display: grid;
    flex: 0 0 auto;
    margin-bottom: $ui-space-16;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: $ui-space-16;
  }

  &__block {
    min-width: 0;
    overflow: hidden;
  }

  &__summary-card-title {
    display: flex;
    min-height: 52px;
    align-items: center;
    justify-content: space-between;
    gap: $ui-space-12;
    padding: $ui-space-8 $ui-space-16;
    border-bottom: 1px solid $ui-line;
    box-sizing: border-box;

    h3 {
      overflow: hidden;
      margin: 0;
      color: $ui-ink;
      font-family: $ui-font-heading;
      font-size: $ui-font-size-section-title;
      font-weight: 600;
      line-height: 22px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__workspace-title {
    display: flex;
    min-height: 64px;
    align-items: center;
    gap: $ui-space-12;
    padding: $ui-space-12 $ui-space-16;
    border-bottom: 1px solid $ui-line;
    box-sizing: border-box;

    > div {
      min-width: 0;
      flex: 1;
    }

    small {
      display: block;
      margin-bottom: $ui-space-4;
      color: $ui-muted;
      font-size: $ui-font-size-metadata;
      font-weight: 600;
      letter-spacing: .04em;
      text-transform: uppercase;
    }

    h3 {
      overflow: hidden;
      margin: 0;
      color: $ui-ink;
      font-family: $ui-font-heading;
      font-size: $ui-font-size-section-title;
      font-weight: 600;
      line-height: 22px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__block-icon {
    display: inline-flex;
    width: 28px;
    height: 28px;
    flex: 0 0 28px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
  }

  &__block-icon--wa {
    background: $ui-green-soft;

    img {
      width: 16px;
      height: 16px;
    }
  }

  &__contact-refresh {
    flex: 0 0 auto;
  }

  &__summary-list {
    display: grid;
    margin: 0;
    padding: $ui-space-12 $ui-space-16;
    gap: $ui-space-8;

    div {
      display: grid;
      min-width: 0;
      align-items: center;
      grid-template-columns: minmax(90px, .75fr) minmax(0, 1.25fr);
      gap: $ui-space-12;
    }

    dt {
      color: $ui-muted;
      font-size: $ui-font-size-body;
    }

    dd {
      min-width: 0;
      margin: 0;
      color: $ui-text;
      font-size: $ui-font-size-body;
      font-weight: 600;
      text-align: right;
      text-overflow: ellipsis;
      overflow-wrap: anywhere;
    }

    &--customer {
      padding-top: $ui-space-8;

      dd {
        display: flex;
        min-width: 0;
        align-items: center;
        justify-content: flex-end;
        gap: $ui-space-8;

        > span {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }
      }
    }
  }

  &__channel-value {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: flex-end;
    gap: $ui-space-8;

    > span:last-child {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__ticket-value {
    display: flex;
    min-width: 0;
    align-items: center;
    justify-content: flex-end;
    gap: $ui-space-8;

    > span {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
  }

  &__external-link {
    display: inline-flex;
    width: $ui-control-height-compact;
    height: $ui-control-height-compact;
    flex: 0 0 $ui-control-height-compact;
    align-items: center;
    justify-content: center;
    border: 1px solid $ui-line-strong;
    border-radius: $ui-control-radius;
    background-color: $ui-surface;
    color: $ui-blue;
    transition: background-color .15s ease, border-color .15s ease;

    &:hover {
      border-color: $ui-blue;
      background-color: $ui-blue-soft;
    }

    &:focus-visible {
      outline: 2px solid $ui-blue;
      outline-offset: 2px;
    }

    svg {
      width: 16px;
      height: 16px;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.5;
    }
  }

  &__copy-action {
    width: $ui-control-height-compact;
    min-width: $ui-control-height-compact;
    flex: 0 0 $ui-control-height-compact;
    padding: 0;

    svg {
      width: 18px;
      height: 18px;
    }
  }

  &__workspace {
    display: grid;
    min-height: 0;
    flex: 1 1 auto;
    grid-template-columns: minmax(0, 1.2fr) minmax(0, .9fr);
    gap: $ui-space-16;
  }

  &__timeline,
  &__decision {
    display: flex;
    min-height: 0;
    flex-direction: column;
  }

  &__timeline :deep(.app-card__body),
  &__decision :deep(.app-card__body) {
    display: flex;
    min-height: 0;
    flex: 1 1 auto;
    flex-direction: column;
  }

  &__workspace-title {
    flex: 0 0 auto;
    align-items: flex-start;
    justify-content: space-between;

    > span {
      flex: 0 0 auto;
      color: $ui-muted;
      font-size: $ui-font-size-body;
    }
  }

  &__decision-title {
    flex-wrap: wrap;

    > div:first-child {
      min-width: 0;
      flex: 1;
    }

    > div:last-child {
      display: flex;
      flex: 0 0 auto;
      align-items: center;
      justify-content: flex-end;
      gap: $ui-space-8;
    }

    p {
      max-width: 580px;
      margin: $ui-space-4 0 0;
      color: $ui-muted;
      font-size: $ui-font-size-body;
      line-height: 20px;
    }
  }

  &__candidate-count {
    padding: $ui-space-4 $ui-space-8;
    border-radius: $ui-control-radius;
    background: $ui-amber-soft;
    color: $ui-amber;
    font-size: $ui-font-size-metadata;
    font-weight: 700;
  }

  &__older,
  &__more {
    margin: $ui-space-12 $ui-space-16 0;
    align-self: flex-start;
  }

  &__messages,
  &__lead-list {
    display: flex;
    min-height: 0;
    flex: 1 1 auto;
    padding: $ui-space-12 $ui-space-16 $ui-space-16;
    overflow-y: auto;
    flex-direction: column;
  }

  &__messages {
    gap: $ui-space-16;
  }

  &__lead-list {
    gap: $ui-space-8;
  }

  &__timeline-date {
    z-index: 1;
    display: flex;
    align-items: center;
    color: $ui-muted;
    font-size: $ui-font-size-body;
    gap: $ui-space-12;

    &::before,
    &::after {
      height: 1px;
      background: $ui-line;
      content: '';
      flex: 1 1 auto;
    }
  }

  &__message {
    width: 76%;
    max-width: 620px;
    align-self: flex-start;

    time {
      display: block;
      margin: $ui-space-4 $ui-space-4 0;
      color: $ui-muted;
      font-size: $ui-font-size-metadata;
      line-height: 16px;
    }

    &--outbound {
      align-self: flex-end;

      time {
        text-align: right;
      }

      .communication-review__message-bubble {
        border-color: $ui-line-strong;
        background: $ui-blue-soft;
      }
    }

    &--note {
      width: 100%;
      max-width: 100%;
      align-self: stretch;

      .communication-review__message-bubble {
        background: $ui-amber-soft;
      }
    }
  }

  &__message-bubble {
    padding: $ui-space-12;
    border: 1px solid $ui-line;
    border-radius: $ui-panel-radius;
    background: $ui-surface-soft;

    p {
      margin: 0;
      color: $ui-text;
      font-size: $ui-font-size-body;
      line-height: 20px;
      white-space: pre-wrap;
      word-break: break-word;
    }
  }

  &__sender {
    display: block;
    margin-bottom: $ui-space-4;
    color: $ui-muted;
    font-size: $ui-font-size-metadata;
    font-weight: 600;
  }

  &__decision > .input {
    width: auto;
    margin: $ui-space-12 $ui-space-16 0;
  }

  &__loading,
  &__inline-error,
  &__empty {
    margin: $ui-space-12 $ui-space-16;
    color: $ui-muted;
    font-size: $ui-font-size-body;
  }

  &__inline-error {
    color: $ui-red;
  }

  &__ignored-details {
    display: grid;
    margin: $ui-space-12 $ui-space-16;
    padding: $ui-space-12;
    border: 1px solid $ui-line-strong;
    border-radius: $ui-panel-radius;
    background: $ui-amber-soft;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: $ui-space-12;

    div {
      display: flex;
      min-width: 0;
      flex-direction: column;
      gap: $ui-space-4;
    }

    span {
      color: $ui-amber;
      font-size: $ui-font-size-metadata;
      text-transform: uppercase;
    }

    strong {
      overflow-wrap: anywhere;
      color: $ui-text;
      font-size: $ui-font-size-body;
    }
  }

  &__actions {
    z-index: 2;
    display: flex;
    min-height: 72px;
    flex: 0 0 auto;
    align-items: center;
    justify-content: flex-end;
    gap: $ui-space-8;
    padding: $ui-space-16 $ui-space-24;
    border-top: 1px solid $ui-line;
    background: $ui-surface;
    box-sizing: border-box;
  }

  &__linked-lead-link {
    text-decoration: none;
  }

  &__dialog-backdrop {
    position: absolute;
    z-index: 20;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: $ui-space-16;
    background: rgba($ui-ink, .42);
    box-sizing: border-box;
  }

  &__dialog {
    display: flex;
    width: 440px;
    max-width: 100%;
    max-height: calc(100% - #{$ui-space-32});
    overflow: hidden;
    border-radius: $ui-overlay-radius;
    background: $ui-surface;
    box-shadow: $ui-card-shadow;
    flex-direction: column;
  }

  &__dialog-body {
    min-height: 0;
    padding: $ui-space-20 $ui-space-24;
    overflow-y: auto;

    > p {
      margin: 0 0 $ui-space-16;
      color: $ui-muted;
      font-size: $ui-font-size-body;
      line-height: 20px;
    }
  }

  &__dialog-field {
    margin-bottom: $ui-space-16;
  }

  &__activities {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: $ui-space-12;
    margin: 0;
    padding: 0;
    border: 0;
  }

  &__dialog-actions {
    display: flex;
    flex: 0 0 auto;
    justify-content: flex-end;
    gap: $ui-space-8;
    padding: $ui-space-16 $ui-space-24;
    border-top: 1px solid $ui-line;
    background: $ui-surface;
  }
}

.communication-review-modal :deep(.vm--modal) {
  overflow: hidden;
  border-radius: $ui-overlay-radius;
  background: $ui-surface-soft;
}

@media (max-width: 1100px) {
  .communication-review {
    &__body {
      overflow-y: auto;
    }

    &__top-grid,
    &__workspace {
      grid-template-columns: 1fr;
    }

    &__workspace {
      flex: 0 0 auto;
    }

    &__timeline,
    &__decision {
      min-height: 420px;
    }
  }
}

@media (max-width: 640px) {
  .communication-review {
    &__body,
    &__actions,
    &__dialog-body,
    &__dialog-actions {
      padding-right: $ui-space-16;
      padding-left: $ui-space-16;
    }

    &__top-grid,
    &__workspace {
      gap: $ui-space-12;
    }

    &__summary-card-title,
    &__workspace-title {
      align-items: flex-start;
      flex-wrap: wrap;
    }

    &__contact-refresh {
      width: 100%;
    }

    &__ignored-details {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    &__message {
      width: 92%;
    }

    &__actions {
      flex-wrap: wrap;

      > * {
        flex: 1 1 100%;
      }
    }
  }
}
</style>
